// src/tabs/logements/ReglesSejourEditor.jsx
//
// Deux règles de séjour STRUCTURÉES (≠ texte libre « Règles de la maison ») :
// « Animaux » et « Fumeur », rendues dans la carte « Règles maison » de
// l'onglet Livret > Instructions.
//
// ── TROIS ÉTATS, PAS DEUX ────────────────────────────────────────────────
// Le besoin métier n'est PAS un booléen : « pas encore répondu » doit rester
// distinguable de « non autorisé ». Un interrupteur à deux positions
// écrirait un `false` implicite sur les 8 logements et ferait annoncer des
// interdictions que Dimitri n'a jamais validées (incident client).
// D'où un <select> à trois options, l'option par défaut étant explicitement
// libellée « Non renseigné » :
//
//   ''    → « Non renseigné » → la clé est ABSENTE de la config du logement
//   'oui' → « Autorisé »      → allows_pets / allows_smoking = true
//   'non' → « Non autorisé »  → allows_pets / allows_smoking = false
//
// Choix du <select> plutôt qu'un groupe de 3 boutons : c'est l'atome déjà
// utilisé par tous les autres choix structurés de la fiche (horaires,
// capacité, composition) — même DA, même clavier, et les trois états sont
// lisibles d'un coup d'œil sans légende.
//
// ── ÉCRITURE ────────────────────────────────────────────────────────────
// `onApplyPatchAndSave` (LogementsPanel.applyPatchAndSaveNow) : save AWAITÉ,
// `{ ok, error }` vérifié, erreur AFFICHÉE — jamais de fire-and-forget ni de
// catch muet. Le retour à « Non renseigné » envoie `undefined` : le spread
// pose la clé à undefined dans le brouillon (le <select> réaffiche bien
// « Non renseigné ») et `JSON.stringify` la SUPPRIME du blob persisté — la
// clé redevient donc réellement absente côté serveur.
// NB : comme le 💾 global, ce save persiste aussi les autres modifications
// en attente du brouillon (sémantique existante de applyPatchAndSaveNow).
//
// Repli sans `onApplyPatchAndSave` : `onUpdate` (brouillon + 💾 Enregistrer),
// le comportement des champs voisins.
//
// ── SOUS-LOCATION ───────────────────────────────────────────────────────
// Aucune cascade : le composant lit et écrit UNIQUEMENT le logement affiché
// (`log`), jamais son parent ni ses sœurs. Chaque chambre lc1/lc2/lc3 porte
// donc ses propres valeurs (ADR-197, chambres autonomes). Les deux champs ne
// sont volontairement PAS ajoutés à la whitelist « Copier vers les chambres »
// (lib/copy-to-chambres) : une copie de masse pourrait poser un `false` sur
// une chambre jamais arbitrée.

import { useState } from "react"
import { Field, Row, Select } from "./atoms"
import { FicheAlert } from "./_ficheUi"
import { DS } from "../../lib/ui"

// Les deux règles rendues, dans l'ordre d'affichage.
export const REGLES_SEJOUR = [
  { field: "allows_pets", label: "🐾 Animaux", testId: "regle-animaux" },
  { field: "allows_smoking", label: "🚭 Fumeur", testId: "regle-fumeur" },
]

const OPTIONS = [
  { value: "", label: "Non renseigné" },
  { value: "oui", label: "Autorisé" },
  { value: "non", label: "Non autorisé" },
]

// cfg → <select>. STRICT : seuls les booléens vrais/faux sont reconnus.
// Tout le reste (clé absente, null, undefined, valeur sale) retombe sur
// « Non renseigné » — jamais sur « Non autorisé ».
export function flagToSelectValue(v) {
  if (v === true) return "oui"
  if (v === false) return "non"
  return ""
}

// <select> → cfg. `undefined` = clé retirée du blob à la sérialisation.
export function selectValueToFlag(v) {
  if (v === "oui") return true
  if (v === "non") return false
  return undefined
}

// Une règle est « renseignée » si et seulement si elle porte un booléen
// strict. Sert à la complétude de la carte (présentation pure).
export function hasRegleSejour(log) {
  return REGLES_SEJOUR.some(
    (r) => log?.[r.field] === true || log?.[r.field] === false,
  )
}

export default function ReglesSejourEditor({ log, onUpdate, onApplyPatchAndSave }) {
  // `savingField` non nul ⇒ les DEUX selects sont désactivés : deux saves
  // concurrents liraient le même brouillon `c` (closure) et le second
  // écraserait la règle enregistrée par le premier.
  const [savingField, setSavingField] = useState(null)
  const [savedField, setSavedField] = useState(null)
  const [error, setError] = useState(null)

  async function handleChange(field, raw) {
    const next = selectValueToFlag(raw)
    setError(null)
    setSavedField(null)

    // Repli (montages sans persistance immédiate) : brouillon + 💾 global.
    if (typeof onApplyPatchAndSave !== "function") {
      onUpdate?.(field, next)
      return
    }

    setSavingField(field)
    try {
      const r = await onApplyPatchAndSave({ [field]: next })
      if (!r || r.ok !== true) {
        // Échec : le brouillon n'a PAS été muté par applyPatchAndSaveNow → le
        // select réaffiche la valeur précédente, aucun état mensonger.
        setError((r && r.error) || "Enregistrement échoué")
        return
      }
      setSavedField(field)
    } catch (e) {
      console.error("[ReglesSejourEditor] save", e)
      setError(e.message || "Enregistrement échoué")
    } finally {
      setSavingField(null)
    }
  }

  return (
    <div
      style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eef1f5" }}
      data-testid="regles-sejour-editor"
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: DS.navy, marginBottom: 4 }}>
        🐾 Animaux · 🚭 Tabac
      </div>
      {/* Texte d'aide = état réel (règle permanente Dimitri). On décrit ce que
          la valeur fait, sans promettre de canal ni de date. */}
      <div style={{ fontSize: 11, color: "#888", lineHeight: 1.5, marginBottom: 10 }}>
        Deux règles structurées, en plus du texte libre ci-dessus.{" "}
        <strong>« Non renseigné »</strong> (le défaut) n'enregistre rien et
        n'annonce rien au voyageur. Seule la valeur{" "}
        <strong>« Non autorisé »</strong> fait annoncer une interdiction
        (« Animaux non acceptés », « Logement non-fumeur ») ;{" "}
        <strong>« Autorisé »</strong> n'annonce rien — on n'annonce que les
        interdictions.
      </div>

      <Row>
        {REGLES_SEJOUR.map((r) => (
          <div key={r.field} data-testid={r.testId}>
            <Field label={r.label}>
              <Select
                value={flagToSelectValue(log?.[r.field])}
                onChange={(v) => handleChange(r.field, v)}
                options={OPTIONS}
                disabled={savingField !== null}
              />
            </Field>
          </div>
        ))}
      </Row>

      {savingField && (
        <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 6 }}>
          Enregistrement…
        </div>
      )}
      {savedField && !error && (
        <FicheAlert tone="success" style={{ marginTop: 6 }} testId="regles-sejour-ok">
          ✓ Règle enregistrée.
        </FicheAlert>
      )}
      {error && (
        <FicheAlert tone="danger" style={{ marginTop: 6 }} testId="regles-sejour-error">
          {error} — la règle n'a PAS été enregistrée, réessayez.
        </FicheAlert>
      )}
    </div>
  )
}
